import React, { useState, useRef } from 'react';
import { adminService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Upload, Trash2, FileText, Edit2, Check, X,
  AlertCircle, Loader2, Eye, EyeOff, FolderOpen,
} from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: 'REGISTRATION', label: 'Business Registration'       },
  { value: 'LICENSING',    label: 'Tourism Licensing & Permits'  },
  { value: 'TAX',          label: 'Tax & Financial Compliance'   },
  { value: 'LEGAL',        label: 'Legal & Contracts'            },
  { value: 'DIGITAL',      label: 'Digital & Online Presence'    },
  { value: 'OTHER',        label: 'Other'                        },
];

const TYPE_STYLES = {
  PDF:   'bg-rust-50 text-rust-600 border border-rust-200',
  DOCX:  'bg-blue-50 text-blue-600 border border-blue-200',
  XLSX:  'bg-green-50 text-green-700 border border-green-200',
  IMAGE: 'bg-amber-50 text-amber-700 border border-amber-200',
  OTHER: 'bg-cream-100 text-forest-500 border border-cream-200',
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Upload Modal ──────────────────────────────────────────────
const UploadModal = ({ onClose, onUploaded }) => {
  const [file,        setFile]        = useState(null);
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [category,    setCategory]    = useState('REGISTRATION');
  const [uploading,   setUploading]   = useState(false);
  const inputRef = useRef();

  const handleSubmit = async () => {
    if (!file)  { toast.error('Please select a file'); return; }
    if (!title) { toast.error('Please enter a title'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title);
      fd.append('description', description);
      fd.append('category', category);
      await adminService.uploadDocument(fd);
      toast.success('Document uploaded successfully');
      onUploaded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-forest-900">Upload Document</h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={18} /></button>
        </div>

        {/* File drop zone */}
        <div
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
            ${file ? 'border-forest-400 bg-forest-50' : 'border-cream-300 hover:border-forest-400'}`}
        >
          <input ref={inputRef} type="file" className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
          {file ? (
            <div className="flex items-center gap-2 justify-center text-forest-700">
              <FileText size={18} />
              <div className="text-left">
                <p className="text-sm font-medium truncate max-w-xs">{file.name}</p>
                <p className="text-xs text-forest-400">{formatSize(file.size)}</p>
              </div>
            </div>
          ) : (
            <>
              <Upload size={24} className="text-forest-300 mx-auto mb-2" />
              <p className="text-sm text-forest-500">Click to select a file</p>
              <p className="text-xs text-forest-400 mt-1">PDF, DOCX, XLSX, or image — max 20MB</p>
            </>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Title *</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Business Name Registration Guide"
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Category *</label>
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white">
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Description <span className="text-forest-400 font-normal">(optional)</span></label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
            placeholder="Brief description of what this document covers..."
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 text-sm py-2.5 rounded-full border border-cream-200 text-forest-600 hover:border-forest-400 transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={uploading}
            className="flex-1 flex items-center justify-center gap-2 text-sm py-2.5 rounded-full bg-forest-700 text-white hover:bg-forest-800 transition-colors font-medium disabled:opacity-60">
            {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading...</> : <><Upload size={14} /> Upload</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Edit inline row ───────────────────────────────────────────
const EditRow = ({ doc, onSave, onCancel }) => {
  const [title,       setTitle]       = useState(doc.title);
  const [description, setDescription] = useState(doc.description || '');
  const [category,    setCategory]    = useState(doc.category);
  const [saving,      setSaving]      = useState(false);

  const handle = async () => {
    setSaving(true);
    try { await onSave(doc.id, { title, description, category }); }
    finally { setSaving(false); }
  };

  return (
    <div className="px-5 py-4 bg-forest-50 border-b border-cream-100 space-y-3">
      <input type="text" value={title} onChange={e => setTitle(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
      <div className="grid grid-cols-2 gap-2">
        <select value={category} onChange={e => setCategory(e.target.value)}
          className="px-3 py-2 rounded-lg border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white">
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Description..."
          className="px-3 py-2 rounded-lg border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="text-xs px-3 py-1.5 rounded-full border border-cream-200 text-forest-600 hover:border-forest-400 transition-colors">Cancel</button>
        <button onClick={handle} disabled={saving}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-forest-700 text-white hover:bg-forest-800 transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Save
        </button>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const AdminDocumentsPage = () => {
  const [showUpload, setShowUpload] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [deleting,   setDeleting]   = useState(null);

  const { data, loading, error, refetch } = useApi(() => adminService.getAllDocuments(), []);
  const docs = data?.documents ?? [];

  const handleSave = async (id, updates) => {
    try {
      await adminService.updateDocument(id, updates);
      toast.success('Document updated');
      setEditing(null);
      refetch();
    } catch { toast.error('Could not update document'); }
  };

  const handleTogglePublish = async (doc) => {
    try {
      await adminService.updateDocument(doc.id, { isPublished: !doc.isPublished });
      toast.success(doc.isPublished ? 'Document hidden from clients' : 'Document visible to clients');
      refetch();
    } catch { toast.error('Could not update document'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await adminService.deleteDocument(id);
      toast.success('Document deleted');
      refetch();
    } catch { toast.error('Could not delete document'); }
    finally { setDeleting(null); }
  };

  // Group by category for display
  const grouped = docs.reduce((acc, doc) => {
    const cat = doc.category || 'OTHER';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  return (
    <DashboardLayout title="Documents">
      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={refetch} />}

      <div className="space-y-5 max-w-4xl">
        <div className="flex items-center justify-between">
          <p className="text-sm text-forest-500">{docs.length} document{docs.length !== 1 ? 's' : ''} uploaded</p>
          <button onClick={() => setShowUpload(true)}
            className="flex items-center gap-2 text-sm font-medium bg-forest-800 text-cream-50 px-5 py-2.5 rounded-full hover:bg-forest-700 transition-colors">
            <Upload size={14} /> Upload Document
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={15} /> Could not load documents.
          </div>
        )}

        {!loading && docs.length === 0 && (
          <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl">
            <FolderOpen size={36} className="text-forest-200 mx-auto mb-3" />
            <p className="font-serif text-xl text-forest-600">No documents yet</p>
            <p className="text-sm text-forest-400 mt-1">Click "Upload Document" to add the first one</p>
          </div>
        )}

        {Object.entries(grouped).map(([cat, catDocs]) => {
          const meta = { REGISTRATION: 'Business Registration', LICENSING: 'Tourism Licensing', TAX: 'Tax & Finance', LEGAL: 'Legal & Contracts', DIGITAL: 'Digital & Online', OTHER: 'Other' };
          return (
            <div key={cat} className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 bg-cream-50 border-b border-cream-100 flex items-center justify-between">
                <p className="text-xs font-semibold text-forest-600 uppercase tracking-wider">{meta[cat] ?? cat}</p>
                <p className="text-xs text-forest-400">{catDocs.length} file{catDocs.length !== 1 ? 's' : ''}</p>
              </div>

              {catDocs.map(doc => (
                <div key={doc.id}>
                  {editing === doc.id ? (
                    <EditRow doc={doc} onSave={handleSave} onCancel={() => setEditing(null)} />
                  ) : (
                    <div className={`flex items-start gap-4 px-5 py-4 border-b border-cream-50 last:border-0 hover:bg-cream-50/40 transition-colors ${!doc.isPublished ? 'opacity-60' : ''}`}>
                      <div className="w-8 h-8 bg-cream-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FileText size={14} className="text-forest-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className="text-sm font-medium text-forest-900">{doc.title}</p>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${TYPE_STYLES[doc.fileType] ?? TYPE_STYLES.OTHER}`}>
                            {doc.fileType}
                          </span>
                          {!doc.isPublished && (
                            <span className="text-[10px] bg-cream-100 text-forest-400 border border-cream-200 px-2 py-0.5 rounded-full">Hidden</span>
                          )}
                          {doc.fileSize && <span className="text-[10px] text-forest-400">{formatSize(doc.fileSize)}</span>}
                        </div>
                        {doc.description && <p className="text-xs text-forest-500">{doc.description}</p>}
                        <p className="text-[10px] text-forest-300 mt-0.5">
                          {doc.uploader ? `Uploaded by ${doc.uploader.firstName} ${doc.uploader.lastName}` : ''}
                          {doc.createdAt ? ` · ${new Date(doc.createdAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 text-forest-400 hover:text-forest-700 hover:bg-cream-100 rounded-lg transition-colors" title="View file">
                          <FileText size={13} />
                        </a>
                        <button onClick={() => handleTogglePublish(doc)} title={doc.isPublished ? 'Hide from clients' : 'Show to clients'}
                          className="p-1.5 text-forest-400 hover:text-forest-700 hover:bg-cream-100 rounded-lg transition-colors">
                          {doc.isPublished ? <Eye size={13} /> : <EyeOff size={13} />}
                        </button>
                        <button onClick={() => setEditing(doc.id)} title="Edit"
                          className="p-1.5 text-forest-400 hover:text-forest-700 hover:bg-cream-100 rounded-lg transition-colors">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(doc.id)} disabled={deleting === doc.id} title="Delete"
                          className="p-1.5 text-forest-300 hover:text-rust-500 hover:bg-rust-50 rounded-lg transition-colors disabled:opacity-40">
                          {deleting === doc.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
};

export default AdminDocumentsPage;